import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  LogOut, Upload, Plus, Trash2, Edit, Save, X, 
  DollarSign, FileText, MessageSquare, Mail, RefreshCw 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState([]);
  const [posts, setPosts] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newPost, setNewPost] = useState(null);
  const [newFaq, setNewFaq] = useState(null);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [ratesRes, postsRes, faqsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/rates`),
        axios.get(`${API}/blog?published_only=false`, getAuthHeader()),
        axios.get(`${API}/faqs`),
        axios.get(`${API}/contact`, getAuthHeader()),
      ]);
      setRates(ratesRes.data);
      setPosts(postsRes.data);
      setFaqs(faqsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    toast.success("Logged out successfully");
    navigate("/admin");
  };

  // CSV Upload
  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/rates/upload-csv`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Rates updated successfully!");
      fetchAllData();
    } catch (error) {
      console.error("CSV upload error:", error);
      toast.error("Failed to upload CSV. Please check the format.");
    }
    e.target.value = "";
  };

  // Blog Post CRUD
  const handleSavePost = async (post) => {
    try {
      if (post.id) {
        await axios.put(`${API}/blog/${post.id}`, post, getAuthHeader());
        toast.success("Post updated!");
      } else {
        await axios.post(`${API}/blog`, post, getAuthHeader());
        toast.success("Post created!");
      }
      setEditingPost(null);
      setNewPost(null);
      fetchAllData();
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error("Failed to save post");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${API}/blog/${postId}`, getAuthHeader());
      toast.success("Post deleted!");
      fetchAllData();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    }
  };

  // FAQ CRUD
  const handleSaveFaq = async (faq) => {
    try {
      if (faq.id) {
        await axios.put(`${API}/faqs/${faq.id}`, faq, getAuthHeader());
        toast.success("FAQ updated!");
      } else {
        await axios.post(`${API}/faqs`, faq, getAuthHeader());
        toast.success("FAQ created!");
      }
      setEditingFaq(null);
      setNewFaq(null);
      fetchAllData();
    } catch (error) {
      console.error("Error saving FAQ:", error);
      toast.error("Failed to save FAQ");
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    try {
      await axios.delete(`${API}/faqs/${faqId}`, getAuthHeader());
      toast.success("FAQ deleted!");
      fetchAllData();
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("Failed to delete FAQ");
    }
  };

  // Contact Delete
  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;
    try {
      await axios.delete(`${API}/contact/${contactId}`, getAuthHeader());
      toast.success("Submission deleted!");
      fetchAllData();
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Failed to delete submission");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="btn-secondary"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="rates" className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="rates" className="data-[state=active]:bg-blue-600" data-testid="tab-rates">
              <DollarSign className="w-4 h-4 mr-2" />
              Rates
            </TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-blue-600" data-testid="tab-blog">
              <FileText className="w-4 h-4 mr-2" />
              Blog
            </TabsTrigger>
            <TabsTrigger value="faqs" className="data-[state=active]:bg-blue-600" data-testid="tab-faqs">
              <MessageSquare className="w-4 h-4 mr-2" />
              FAQs
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-blue-600" data-testid="tab-contacts">
              <Mail className="w-4 h-4 mr-2" />
              Contacts
            </TabsTrigger>
          </TabsList>

          {/* Rates Tab */}
          <TabsContent value="rates" className="space-y-6">
            <div className="card-glass">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Currency Rates</h2>
                <div className="flex items-center gap-4">
                  <label className="btn-primary cursor-pointer flex items-center gap-2 px-4 py-2 rounded-lg">
                    <Upload className="w-4 h-4" />
                    Upload CSV
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="hidden"
                      data-testid="csv-upload-input"
                    />
                  </label>
                </div>
              </div>

              <div className="mb-4 p-4 bg-slate-800/50 rounded-lg">
                <p className="text-sm text-slate-400">
                  <strong>CSV Format:</strong> currency_code, currency_name, buy_rate, sell_rate, is_crypto
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Example: BTC,Bitcoin,103500.00,102800.00,true
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Currency</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Buy Rate</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Sell Rate</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((rate) => (
                      <tr key={rate.id} className="border-b border-slate-800/50" data-testid={`rate-row-admin-${rate.currency_code}`}>
                        <td className="py-3 px-4">
                          <span className="font-semibold text-white">{rate.currency_code}</span>
                          <span className="text-slate-500 text-sm ml-2">{rate.currency_name}</span>
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-emerald-400">{rate.buy_rate}</td>
                        <td className="text-right py-3 px-4 font-mono text-rose-400">{rate.sell_rate}</td>
                        <td className="text-center py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs ${rate.is_crypto ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}`}>
                            {rate.is_crypto ? 'Crypto' : 'Fiat'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog" className="space-y-6">
            <div className="card-glass">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Blog Posts</h2>
                <Button 
                  onClick={() => setNewPost({ title: "", slug: "", excerpt: "", content: "", author: "FonderX Team", image_url: "", published: true })}
                  className="btn-primary"
                  data-testid="add-post-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Post
                </Button>
              </div>

              {/* New Post Form */}
              {newPost && (
                <PostForm 
                  post={newPost} 
                  onSave={handleSavePost} 
                  onCancel={() => setNewPost(null)} 
                />
              )}

              {/* Edit Post Form */}
              {editingPost && (
                <PostForm 
                  post={editingPost} 
                  onSave={handleSavePost} 
                  onCancel={() => setEditingPost(null)} 
                />
              )}

              {/* Posts List */}
              <div className="space-y-4">
                {posts.map((post) => (
                  <div key={post.id} className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg" data-testid={`post-item-${post.slug}`}>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{post.title}</h3>
                      <p className="text-slate-500 text-sm">{post.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${post.published ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-500/10 text-slate-500'}`}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setEditingPost(post)} data-testid={`edit-post-${post.slug}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePost(post.id)} className="text-red-500 hover:text-red-400" data-testid={`delete-post-${post.slug}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* FAQs Tab */}
          <TabsContent value="faqs" className="space-y-6">
            <div className="card-glass">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">FAQs</h2>
                <Button 
                  onClick={() => setNewFaq({ question: "", answer: "", order: faqs.length + 1 })}
                  className="btn-primary"
                  data-testid="add-faq-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add FAQ
                </Button>
              </div>

              {/* New FAQ Form */}
              {newFaq && (
                <FaqForm 
                  faq={newFaq} 
                  onSave={handleSaveFaq} 
                  onCancel={() => setNewFaq(null)} 
                />
              )}

              {/* Edit FAQ Form */}
              {editingFaq && (
                <FaqForm 
                  faq={editingFaq} 
                  onSave={handleSaveFaq} 
                  onCancel={() => setEditingFaq(null)} 
                />
              )}

              {/* FAQs List */}
              <div className="space-y-4">
                {faqs.map((faq) => (
                  <div key={faq.id} className="flex items-start justify-between p-4 bg-slate-800/30 rounded-lg" data-testid={`faq-item-${faq.id}`}>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{faq.question}</h3>
                      <p className="text-slate-400 text-sm mt-1 line-clamp-2">{faq.answer}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button variant="ghost" size="sm" onClick={() => setEditingFaq(faq)} data-testid={`edit-faq-${faq.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteFaq(faq.id)} className="text-red-500 hover:text-red-400" data-testid={`delete-faq-${faq.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-6">
            <div className="card-glass">
              <h2 className="text-xl font-semibold text-white mb-6">Contact Submissions</h2>
              
              {contacts.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No contact submissions yet.</p>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="p-4 bg-slate-800/30 rounded-lg" data-testid={`contact-item-${contact.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-white">{contact.subject}</h3>
                          <p className="text-slate-400 text-sm">{contact.name} • {contact.email}</p>
                          {contact.phone && <p className="text-slate-500 text-xs">{contact.phone}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 text-xs">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(contact.id)} className="text-red-500 hover:text-red-400" data-testid={`delete-contact-${contact.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-slate-300 text-sm mt-3 p-3 bg-slate-900/50 rounded">{contact.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Post Form Component
function PostForm({ post, onSave, onCancel }) {
  const [formData, setFormData] = useState(post);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Auto-generate slug from title
    if (name === "title") {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      setFormData(prev => ({ ...prev, title: value, slug }));
    }
  };

  return (
    <div className="mb-6 p-6 bg-slate-800/50 rounded-lg border border-slate-700" data-testid="post-form">
      <h3 className="text-lg font-semibold text-white mb-4">{post.id ? "Edit Post" : "New Post"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Title</Label>
          <Input name="title" value={formData.title} onChange={handleChange} className="input-dark" data-testid="post-title-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Slug</Label>
          <Input name="slug" value={formData.slug} onChange={handleChange} className="input-dark" data-testid="post-slug-input" />
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <Label className="text-slate-300">Excerpt</Label>
        <Input name="excerpt" value={formData.excerpt} onChange={handleChange} className="input-dark" data-testid="post-excerpt-input" />
      </div>
      <div className="space-y-2 mb-4">
        <Label className="text-slate-300">Content</Label>
        <Textarea name="content" value={formData.content} onChange={handleChange} className="input-dark min-h-[200px]" data-testid="post-content-input" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Image URL</Label>
          <Input name="image_url" value={formData.image_url || ""} onChange={handleChange} className="input-dark" data-testid="post-image-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Author</Label>
          <Input name="author" value={formData.author} onChange={handleChange} className="input-dark" data-testid="post-author-input" />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <Switch checked={formData.published} onCheckedChange={(checked) => setFormData({ ...formData, published: checked })} data-testid="post-published-switch" />
        <Label className="text-slate-300">Published</Label>
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(formData)} className="btn-primary" data-testid="save-post-btn">
          <Save className="w-4 h-4 mr-2" />
          Save
        </Button>
        <Button variant="outline" onClick={onCancel} className="btn-secondary" data-testid="cancel-post-btn">
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </div>
  );
}

// FAQ Form Component
function FaqForm({ faq, onSave, onCancel }) {
  const [formData, setFormData] = useState(faq);

  return (
    <div className="mb-6 p-6 bg-slate-800/50 rounded-lg border border-slate-700" data-testid="faq-form">
      <h3 className="text-lg font-semibold text-white mb-4">{faq.id ? "Edit FAQ" : "New FAQ"}</h3>
      <div className="space-y-2 mb-4">
        <Label className="text-slate-300">Question</Label>
        <Input value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} className="input-dark" data-testid="faq-question-input" />
      </div>
      <div className="space-y-2 mb-4">
        <Label className="text-slate-300">Answer</Label>
        <Textarea value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} className="input-dark min-h-[100px]" data-testid="faq-answer-input" />
      </div>
      <div className="space-y-2 mb-4">
        <Label className="text-slate-300">Order</Label>
        <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} className="input-dark w-24" data-testid="faq-order-input" />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(formData)} className="btn-primary" data-testid="save-faq-btn">
          <Save className="w-4 h-4 mr-2" />
          Save
        </Button>
        <Button variant="outline" onClick={onCancel} className="btn-secondary" data-testid="cancel-faq-btn">
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </div>
  );
}
